import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export enum ParcelTrackingEventType {
  // Pickup
  PARCEL_CREATED = "PARCEL_CREATED",
  ORDER_PROCESSED = "ORDER_PROCESSED",
  PICKUP_SCHEDULED = "PICKUP_SCHEDULED",
  OUT_FOR_PICKUP = "OUT_FOR_PICKUP",
  PICKED_UP = "PICKED_UP",
  HELD_FOR_PICKUP = "HELD_FOR_PICKUP",
  PICKUP_EXCEPTION = "PICKUP_EXCEPTION",
  PICKUP_CANCELLED = "PICKUP_CANCELLED",

  // Customs
  CLEARED_CUSTOMS = "CLEARED_CUSTOMS",
  CUSTOMS_DELAY = "CUSTOMS_DELAY",

  // Delivery
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED = "DELIVERED",
  DELIVERY_EXCEPTION = "DELIVERY_EXCEPTION",
  DELIVERY_CANCELLED = "DELIVERY_CANCELLED",

  // Return
  RETURNED_TO_FACILITY = "RETURNED_TO_FACILITY",
  RETURN_TO_SENDER = "RETURN_TO_SENDER",
  RETURN_EXCEPTION = "RETURN_EXCEPTION",
  RETURN_CANCELLED = "RETURN_CANCELLED",

  // Depot
  DEPOT_RECEIVE_EXCEPTION = "DEPOT_RECEIVE_EXCEPTION",
  DEPOT_RECEIVE_CANCELLED = "DEPOT_RECEIVE_CANCELLED",
  DEPOT_SORT_EXCEPTION = "DEPOT_SORT_EXCEPTION",
  DEPOT_SORT_CANCELLED = "DEPOT_SORT_CANCELLED",
  DEPOT_DISPATCH_EXCEPTION = "DEPOT_DISPATCH_EXCEPTION",
  DEPOT_DISPATCH_CANCELLED = "DEPOT_DISPATCH_CANCELLED",
  ARRIVED_AT_FACILITY = "ARRIVED_AT_FACILITY",
  IN_TRANSIT = "IN_TRANSIT",
  DEPARTED_FACILITY = "DEPARTED_FACILITY",
  PROCESSING_AT_FACILITY = "PROCESSING_AT_FACILITY",
}

export interface ParcelTrackingEvent {
  id: string;
  parcelId: string;
  trackingNumber: string;
  eventType: ParcelTrackingEventType;
  eventName: string;
  eventDescription: string;
  timestamp: string;
  metadata: {
    location: {
      city: string;
      province: string;
      country: string;
    };
  } & Record<string, any>;
  notes: string;
  parcel: {
    orderId: string;
    order: {
      senderName: string;
      recipientName: string;
      pickupAddress: {
        line1: string;
        line2: string;
        city: string;
        province: string;
        postalCode: string;
        country: string;
        location: {
          latitude: number;
          longitude: number;
        };
      };
      deliveryAddress: {
        line1: string;
        line2: string;
        city: string;
        province: string;
        postalCode: string;
        country: string;
        location: {
          latitude: number;
          longitude: number;
        };
      };
    };
  };
  task: {
    address: {
      line1: string;
      line2: string;
      city: string;
      province: string;
      postalCode: string;
      country: string;
      location: {
        latitude: number;
        longitude: number;
      };
    };
    proof: {
      notes: string;
      createdAt: string;
      updatedAt: string;
      mediaItems: {
        id: string;
        url: string;
        type: string;
      }[];
    };
    operator: {
      user: {
        firstName: string;
      };
    };
  };
}

// Response type for tracking events API
export interface TrackingEventsResponse {
  trackingEvents: ParcelTrackingEvent[];
  hasSensitiveInfo: boolean;
  message: string;
}

// Options for the tracking API
export interface TrackingApiOptions {
  token?: string;
}

export function useParcelTrackingEvents(
  trackingNumber: string,
  options?: TrackingApiOptions
) {
  return useQuery({
    queryKey: ["parcelTrackingEvents", trackingNumber, options?.token],
    queryFn: async () => {
      // Build URL with query parameters if token is provided
      let url = `${import.meta.env.VITE_API_URL}/api/v1/parcels/${trackingNumber}/tracking-events`;

      // Add token as query parameter if provided
      if (options?.token) {
        url += `?token=${options.token}`;
      }

      const response = await axios.get<TrackingEventsResponse>(url);
      return response.data;
    },
    enabled: !!trackingNumber,
  });
}

export interface ParcelOperatorLocation {
  location: {
    lat: number;
    lng: number;
  };
  stopsAway: number;
  estimatedArrival: string;
  path: {
    origin: {
      lat: number;
      lng: number;
    };
    destination: {
      lat: number;
      lng: number;
    };
    waypoints: {
      lat: number;
      lng: number;
    }[];
  };
}

export function useParcelLiveTracking(
  trackingNumber: string,
  options?: { token?: string }
) {
  return useQuery({
    queryKey: ["parcelOperatorLocation", trackingNumber, options?.token],
    queryFn: async () => {
      try {
        // Build the URL with the token as a query parameter if available
        let url = `${import.meta.env.VITE_API_URL}/api/v1/tracking/${trackingNumber}/live-tracking`;
        if (options?.token) {
          url += `?token=${options.token}`;
        }

        const response = await fetch(url);

        if (response.status === 403) {
          // Authentication required
          return { requiresAuth: true, error: "Authentication required" };
        }

        if (!response.ok) {
          throw new Error(
            `Failed to fetch operator location: ${response.status}`
          );
        }

        return (await response.json()) as ParcelOperatorLocation;
      } catch (error) {
        console.error("Error fetching operator location:", error);
        throw error;
      }
    },
    // Refresh every 30 seconds
    refetchInterval: 30000,
    enabled: !!trackingNumber && !!options?.token,
  });
}
